import React, { Component } from "react";
import PropTypes from "prop-types";
import GlobalNavigation, {
  AppSwitcherSidePanel,
  CurrentApp,
  HelperWithPendo,
  KeyboardShortcutPanel,
  LaunchpadLink,
  Logo,
  SignIn,
  User,
  appSwitcherPropsShape,
} from "@acl-services/sriracha-global-navigation";
import i18n from "@viz-ui/i18n/i18n";
import { getLoganBasePath } from "@viz-ui/services/storyboardUrlDetection/storyboardUrlDetection";
import backendApi from "@results/services/apiCall/backendApi";
import "./GlobalNavBar.scss";

const NAV_ITEMS = {
  DEFAULT: ["CurrentApp", "LaunchpadLink", "AppSwitcher", "Helper", "User", "KeyboardShortcutPanel"],
  PUBLIC: ["Logo", "SignIn", "Helper"],
};

const GLOBAL_NAV_ZINDEX = 210;

export default class GlobalNavBar extends Component {
  state = { isLoaded: false, data: null };

  componentDidMount() {
    this.fetchDataFromLaunchPad();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userFullName !== this.props.userFullName) {
      this.fetchDataFromLaunchPad();
    }
  }

  getNavItem() {
    const { links, appSwitcherProps, currentAppId } = this.props;

    return itemName => {
      switch (itemName) {
        case "Logo":
          return <Logo key={itemName} launchpadUrl={links.launchpad} />;
        case "CurrentApp":
          return (
            <CurrentApp
              key={itemName}
              appId={currentAppId}
              appUrl={this.currentAppUrl()}
              appModuleName={this.currentAppModuleName()}
            />
          );
        case "LaunchpadLink": {
          const organizationName = appSwitcherProps.organizations.find(
            org => org.id === appSwitcherProps.initialOrganizationId
          ).name;
          return <LaunchpadLink key={itemName} launchpadUrl={links.launchpad} orgName={organizationName} />;
        }
        case "KeyboardShortcutPanel":
          return <KeyboardShortcutPanel key={itemName} appId={currentAppId} />;
        case "AppSwitcher":
          return <AppSwitcherSidePanel key={itemName} {...appSwitcherProps} />;
        case "SignIn":
          return <SignIn key={itemName} loginUrl={links.login} />;
        case "Helper":
          return <HelperWithPendo key={itemName} helpDocUrl={links.help.results} supportUrl={links.support} />;
        case "User":
          return (
            <User
              key={itemName}
              customerName={this.props.customerName}
              username={this.props.userFullName}
              userProfileUrl={links.profile}
              logoutUrl={links.logout}
            />
          );
        default:
          throw new Error("Unsupported nav item");
      }
    };
  }

  currentAppModuleName() {
    const currentAppId = this.props.currentAppId === "storyboards" ? "storyboards.plural" : "activities.results_title";
    return i18n.t(currentAppId);
  }

  currentAppUrl() {
    return this.props.currentAppId === "storyboards" ? `${getLoganBasePath()}/storyboards` : "/";
  }

  async fetchDataFromLaunchPad() {
    if (this.isLoggedIn()) {
      const response = await backendApi.publicApiGet(`/accounts/api/global_nav_bar`);
      if (response) {
        const { data } = response.data;
        this.setState({
          data,
          isLoaded: true,
        });
      }
    }
  }

  isLoggedIn() {
    return !!this.props.userFullName;
  }

  renderNavItems() {
    if (this.isLoggedIn()) {
      return NAV_ITEMS.DEFAULT.map(this.getNavItem());
    }
    return NAV_ITEMS.PUBLIC.map(this.getNavItem());
  }

  renderLoggedInGlobalNav() {
    if (!this.state.isLoaded) {
      return (
        <GlobalNavigation
          mainContentSelector="main, [role=main]"
          locale={i18n.locale}
          zIndex={GLOBAL_NAV_ZINDEX}
          isDiligentBrand
        />
      );
    }

    const { currentOrgSubscriptionType } = this.state.data;

    return (
      <GlobalNavigation
        currentOrgSubscriptionType={currentOrgSubscriptionType}
        zIndex={GLOBAL_NAV_ZINDEX}
        mainContentSelector="main, [role=main]"
        locale={i18n.locale}
      >
        {this.renderNavItems()}
      </GlobalNavigation>
    );
  }

  renderLoggedOutGlobalNav() {
    return (
      <GlobalNavigation
        zIndex={GLOBAL_NAV_ZINDEX}
        mainContentSelector="main, [role=main]"
        locale={i18n.locale}
        isDiligentBrand
      >
        {this.renderNavItems()}
      </GlobalNavigation>
    );
  }

  render() {
    if (this.isLoggedIn()) {
      return this.renderLoggedInGlobalNav();
    }
    return this.renderLoggedOutGlobalNav();
  }
}

GlobalNavBar.propTypes = {
  appSwitcherProps: PropTypes.shape(appSwitcherPropsShape).isRequired,
  customerName: PropTypes.string,
  currentAppId: PropTypes.string.isRequired,
  links: PropTypes.shape({
    help: PropTypes.shape({
      results: PropTypes.string.isRequired,
    }).isRequired,
    support: PropTypes.string.isRequired,
    launchpad: PropTypes.string.isRequired,
    logout: PropTypes.string.isRequired,
    profile: PropTypes.string.isRequired,
    login: PropTypes.string.isRequired,
  }).isRequired,
  locale: PropTypes.string,
  userFullName: PropTypes.string.isRequired,
};

GlobalNavBar.defaultProps = {
  customerName: null,
  locale: "en",
};
